import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import type { IPendingInvitation } from '@/services/AccessControlApi';
import {
  createAttentionPoller,
  type AttentionPoller,
  type AttentionSubscriber,
} from './attentionPoller';

/**
 * The reads this store makes. Handed in (`stores/index.ts`), so the launch entry carries neither the
 * access API nor the preferences API: each is fetched with the first read.
 */
export interface IInvitationsWaitingApi {
  /** `GET /invitations/mine`: the person's own pending invitations. */
  mine(): Promise<unknown>;
  /** `GET /user-preferences/invitations-seen`: the ids whose notice they have been shown. */
  seen(): Promise<unknown>;
  /** `PUT /user-preferences/invitations-seen/:id`. */
  markSeen(id: string): Promise<unknown>;
}

/** How long the notice that an invitation is waiting stays up (I-2233), a little over the IU toasts' 8 s. */
export const INVITATION_NOTICE_MS = 10_000;

export interface IInvitationsWaitingStoreOptions {
  userId: () => string | null;
  api: IInvitationsWaitingApi;
  /** The attention stream; only its resume is used, to read again when the app comes back. */
  subscribeToAttention?: AttentionSubscriber;
}

/**
 * I-2233 (`docs/concept-invitations-to-several-work-areas.md` AC-PI.2 to AC-PI.5): the signed-in
 * person's pending invitations, wherever they are in the app. Jay, 2026-10-27: "if nothing is there
 * yet show a notification on the profile line"; then "dont put invitations on home. we can have a
 * notification snack panel that leads to the settings view".
 *
 * One store, read by the profile line's badge, the phone menu's profile row and the notice that a
 * new one is waiting, so the three never disagree. It re-reads on the app's one loop for such counts
 * (`createAttentionPoller`: every 30 s while the window is in front, every minute behind it, at once
 * when the reader comes back) — never a loop of its own.
 *
 * **Seen** is kept with the person on the server (`user-preferences/invitations-seen`), never on the
 * device: the notice appears once per invitation, and not again on the next page load or on their
 * other devices. Until the seen list has been read, nothing is announced, so a failed read never
 * turns into a notice on every page.
 */
export class InvitationsWaitingStore {
  /** `null` until the first read for this person has answered. */
  invitations: IPendingInvitation[] | null = null;
  /** The first read failed and nothing is known: the badge then says nothing. */
  failed = false;
  /** The ids already announced to this person; `null` until read. */
  seen: ReadonlySet<string> | null = null;
  /** The invitations the notice is saying are new, while it is up. */
  announcement: IPendingInvitation[] | null = null;

  private poller: AttentionPoller | null = null;
  private unsubscribeAttention: (() => void) | null = null;
  private serial = 0;
  private seenFor: string | null = null;
  private noticeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly options: IInvitationsWaitingStoreOptions) {
    makeObservable<InvitationsWaitingStore>(this, {
      invitations: observable.ref,
      failed: observable,
      seen: observable.ref,
      announcement: observable.ref,
      count: computed,
      unannounced: computed,
      refresh: action,
      forget: action,
      drop: action,
      markAnnounced: action,
      announce: action,
      dismissAnnouncement: action,
    });
    if (typeof window !== 'undefined') {
      this.poller = createAttentionPoller({ read: () => this.refresh() });
      this.unsubscribeAttention =
        options.subscribeToAttention?.({ onResume: () => this.poller?.soon(0) }) ?? null;
    }
  }

  /** The badge's number: `undefined` while the first read is in flight or after it failed. */
  get count(): number | undefined {
    return this.invitations?.length;
  }

  /** Pending invitations whose notice this person has not been shown. Empty until `seen` is known. */
  get unannounced(): IPendingInvitation[] {
    const seen = this.seen;
    if (!seen || !this.invitations) return [];
    return this.invitations.filter((invitation) => !seen.has(invitation.id));
  }

  /** Reads the list (and, once per person, the seen ids). Signed out, it reads nothing. */
  async refresh(): Promise<void> {
    const userId = this.options.userId();
    if (!userId) return;
    const serial = ++this.serial;
    const readSeen = this.seenFor !== userId;
    const [mine, seen] = await Promise.allSettled([
      this.options.api.mine(),
      readSeen ? this.options.api.seen() : Promise.resolve(null),
    ]);
    // An answer for a person who has gone since, or overtaken by a later read, is nobody's here.
    if (serial !== this.serial || this.options.userId() !== userId) return;
    runInAction(() => {
      if (mine.status === 'fulfilled' && Array.isArray(mine.value)) {
        this.invitations = mine.value as IPendingInvitation[];
        this.failed = false;
      } else if (this.invitations === null) {
        // An answer that is not a list (a proxy's `{}`, an error body) is a list that cannot be read.
        this.failed = true;
      }
      if (readSeen && seen.status === 'fulfilled') {
        const ids = (seen.value as { seen?: unknown } | null)?.seen;
        if (Array.isArray(ids)) {
          this.seen = new Set(ids.filter((id): id is string => typeof id === 'string'));
          this.seenFor = userId;
        }
      }
    });
  }

  /** Another person, or none: nothing of the last one's stays. */
  forget(): void {
    this.serial += 1;
    this.invitations = null;
    this.failed = false;
    this.seen = null;
    this.seenFor = null;
    this.dismissAnnouncement();
  }

  /** An invitation accepted or declined here leaves the badge at once, before the next read. */
  drop(id: string): void {
    if (this.invitations) {
      this.invitations = this.invitations.filter((invitation) => invitation.id !== id);
    }
  }

  /**
   * The notice for these was shown (or their list was on screen): never again. Kept here at once and
   * written to the person's preferences; a write that fails costs at most one more notice.
   */
  markAnnounced(ids: readonly string[]): void {
    const seen = new Set(this.seen ?? []);
    const fresh = ids.filter((id) => !seen.has(id));
    if (fresh.length === 0) return;
    for (const id of fresh) seen.add(id);
    this.seen = seen;
    for (const id of fresh) void this.options.api.markSeen(id).catch(() => undefined);
  }

  /**
   * AC-PI.4: say once that these invitations are new, for `INVITATION_NOTICE_MS`, and never again.
   * `quiet`: the person is looking at Settings → Invitations, where the list says it already — they
   * are marked seen without a notice.
   */
  announce({ quiet = false }: { quiet?: boolean } = {}): void {
    const fresh = this.unannounced;
    if (fresh.length === 0) return;
    this.markAnnounced(fresh.map((invitation) => invitation.id));
    if (quiet) return;
    if (this.noticeTimer) clearTimeout(this.noticeTimer);
    this.announcement = fresh;
    this.noticeTimer = setTimeout(
      () => runInAction(() => this.dismissAnnouncement()),
      INVITATION_NOTICE_MS,
    );
  }

  dismissAnnouncement(): void {
    if (this.noticeTimer) {
      clearTimeout(this.noticeTimer);
      this.noticeTimer = null;
    }
    this.announcement = null;
  }

  /** Tests only. */
  dispose(): void {
    this.dismissAnnouncement();
    this.poller?.dispose();
    this.unsubscribeAttention?.();
  }
}
